/**
 * Rasterize business logo for Volcora POS_Output_PrintBmpDirectA (Windows BMP, 1 bpp monochrome).
 */
import axios from 'axios'

const DEFAULT_LOGO_MAX_HEIGHT = 120

/**
 * @param {number} paperWidthMm
 * @returns {number} max raster width in pixels (common thermal head resolutions)
 */
export function getThermalLogoMaxWidth (paperWidthMm) {
  return Number(paperWidthMm) === 58 ? 384 : 576
}

/** Presigned S3 logo URLs from `/business-info` expire in ~10 minutes. */
const LOGO_BMP_CACHE_MS = 8 * 60 * 1000

/** Bump when ESC/POS raster polarity or BMP encoding changes (cache bust). */
const LOGO_BMP_CACHE_VERSION = 'escpos-v2'

/** @type {{ key: string, bmp: string|null, expiresAt: number }} */
let logoBmpCache = { key: '', bmp: null, expiresAt: 0 }

/** Clear cached logo BMP (e.g. after logo upload or polarity fix deploy). */
export function clearLogoBmpCache () {
  logoBmpCache = { key: '', bmp: null, expiresAt: 0 }
}

/**
 * Stable cache key for presigned logo URLs (pathname ignores rotating query params).
 * @param {string} url
 */
export function logoObjectKeyFromUrl (url) {
  try {
    return new URL(url).pathname
  } catch {
    return String(url || '')
  }
}

/** @param {unknown} businessInfo */
export function businessInfoForThermal (businessInfo) {
  if (!businessInfo || typeof businessInfo !== 'object') {
    return null
  }
  const info = /** @type {Record<string, unknown>} */ (businessInfo)
  const name = String(info.name || info.business_name || '').trim()
  return {
    ...info,
    name: name || undefined
  }
}

/**
 * Reload business info so logo_url / logo_original_url presigned URLs are fresh.
 * @returns {Promise<ReturnType<typeof businessInfoForThermal>>}
 */
export async function refreshBusinessInfoForThermal () {
  const { useBusinessStore } = await import('@/stores/business')
  const businessStore = useBusinessStore()
  try {
    await businessStore.fetchInfo({ force: true })
  } catch (e) {
    if (import.meta.env.QUASAR_DEV) {
      console.warn('[Thermal] business info refresh failed:', e?.message || e)
    }
  }
  return businessInfoForThermal(businessStore.info)
}

/**
 * @param {unknown} businessInfo
 * @param {number} paperWidthMm
 * @param {string} [logoUrlFallback]
 * @param {{ refreshOnMiss?: boolean }} [opts]
 */
export async function resolveThermalLogoBmp (
  businessInfo,
  paperWidthMm,
  logoUrlFallback = null,
  opts = {}
) {
  const refreshOnMiss = opts.refreshOnMiss !== false

  async function rasterize (info) {
    let rawLogo = pickBusinessLogoUrl(info)
    if (!rawLogo && logoUrlFallback) {
      rawLogo = String(logoUrlFallback).trim()
    }
    if (!rawLogo) {
      if (import.meta.env.QUASAR_DEV) {
        console.warn('[Thermal] logo rasterize skipped: no logo URL on business info')
      }
      return null
    }
    return fetchBusinessLogoAsBmpBase64(rawLogo, paperWidthMm)
  }

  let logoBmpBase64 = await rasterize(businessInfo)
  if (logoBmpBase64) {
    return logoBmpBase64
  }

  if (!refreshOnMiss) {
    return null
  }

  const freshInfo = await refreshBusinessInfoForThermal()
  if (freshInfo) {
    logoBmpBase64 = await rasterize(freshInfo)
  }
  if (!logoBmpBase64 && import.meta.env.QUASAR_DEV) {
    console.warn('[Thermal] logo rasterize failed after business info refresh')
  }
  return logoBmpBase64
}

/** @param {unknown} businessInfo */
export function pickBusinessLogoUrl (businessInfo) {
  if (!businessInfo || typeof businessInfo !== 'object') return ''
  const u = businessInfo.logo_url || businessInfo.logo_original_url || ''
  const s = String(u).trim()
  if (!s) return ''
  if (s.includes('default-business')) return ''
  return s
}

/**
 * Resolve logo URL for axios/fetch (API may return site-relative paths).
 * @param {string} raw
 * @returns {string}
 */
export function resolveLogoRequestUrl (raw) {
  const s = String(raw || '').trim()
  if (!s) return ''
  if (/^https?:\/\//i.test(s)) {
    return s
  }
  const apiBase = String(axios.defaults.baseURL || '').replace(/\/+$/, '')
  const origin = apiBase.replace(/\/api$/, '')
  if (!origin) {
    return s.startsWith('/') ? s : `/${s}`
  }
  const path = s.startsWith('/') ? s : `/${s}`
  return `${origin}${path}`
}

/**
 * Pack RGBA image data into Windows BMP 1bpp (BI_RGB), bottom-up, MSB-first within byte.
 * Pixel: 0 = black (ink), 1 = white (paper).
 * @param {number} width
 * @param {number} height
 * @param {Uint8ClampedArray} rgba length width*height*4
 * @returns {string} base64
 */
export function rgbaToMonoBmpBase64 (width, height, rgba) {
  const rowStride = Math.ceil(width / 32) * 4
  const pixelBytes = rowStride * height
  const headerSize = 62
  const total = headerSize + pixelBytes
  const u8 = new Uint8Array(total)
  const v = new DataView(u8.buffer)
  u8[0] = 0x42
  u8[1] = 0x4d
  v.setUint32(2, total, true)
  v.setUint32(10, headerSize, true)
  v.setUint32(14, 40, true)
  v.setInt32(18, width, true)
  v.setInt32(22, height, true)
  v.setUint16(26, 1, true)
  v.setUint16(28, 1, true)
  v.setUint32(30, 0, true)
  v.setUint32(34, pixelBytes, true)
  v.setUint32(38, 0, true)
  v.setUint32(42, 0, true)
  v.setUint32(46, 2, true)
  v.setUint32(50, 0, true)
  u8[54] = 0
  u8[55] = 0
  u8[56] = 0
  u8[57] = 0
  u8[58] = 255
  u8[59] = 255
  u8[60] = 255
  u8[61] = 0

  const lum = (i) => {
    const r = rgba[i]
    const g = rgba[i + 1]
    const b = rgba[i + 2]
    return 0.299 * r + 0.587 * g + 0.114 * b
  }

  let off = 62
  for (let y = height - 1; y >= 0; y--) {
    let x = 0
    while (x < width) {
      let byte = 0
      for (let bit = 0; bit < 8; bit++) {
        const xx = x + bit
        let px = 1
        if (xx < width) {
          const L = lum((y * width + xx) * 4)
          px = L < 140 ? 0 : 1
        }
        byte |= (px & 1) << (7 - bit)
      }
      u8[off++] = byte
      x += 8
    }
    while ((off - 62) % rowStride !== 0) {
      u8[off++] = 0xff
    }
  }

  let binary = ''
  const chunk = 0x8000
  for (let i = 0; i < u8.length; i += chunk) {
    binary += String.fromCharCode.apply(null, u8.subarray(i, Math.min(i + chunk, u8.length)))
  }
  return btoa(binary)
}

/**
 * Scale the logo to fit within maxWidth × maxHeight, then composite onto a full-width strip
 * (thermal head width) so POS_Output_PrintBmpDirectA - which ignores text alignment - still
 * prints centered on the receipt.
 * @param {ImageBitmap} bitmap
 * @param {number} maxWidth full raster width for paper (576 / 384)
 * @param {number} maxHeight
 * @returns {string} base64 BMP
 */
export function imageBitmapToMonoBmpBase64 (bitmap, maxWidth, maxHeight) {
  const mw = Math.max(8, Number(maxWidth) || 576)
  const mh = Math.max(16, Number(maxHeight) || 120)
  const scale = Math.min(mw / bitmap.width, mh / bitmap.height, 1)
  const w = Math.max(1, Math.round(bitmap.width * scale))
  const h = Math.max(1, Math.round(bitmap.height * scale))

  const canvas = document.createElement('canvas')
  canvas.width = mw
  canvas.height = h
  const ctx = canvas.getContext('2d')
  if (!ctx) {
    return ''
  }
  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, mw, h)
  const xOff = Math.max(0, Math.round((mw - w) / 2))
  ctx.drawImage(bitmap, xOff, 0, w, h)
  const { data } = ctx.getImageData(0, 0, mw, h)
  return rgbaToMonoBmpBase64(mw, h, data)
}

/**
 * Logo bytes for thermal raster: Electron uses main-process fetch (avoids CORS + bad cache hits).
 * Browser / Capacitor use axios with cache-bypass headers.
 * @param {string} url absolute or resolved URL
 * @returns {Promise<Blob>}
 */
async function fetchLogoImageBlob (url) {
  const api = typeof window !== 'undefined' ? window.electronAPI : null
  if (api?.fetchBinaryUrl) {
    const r = await api.fetchBinaryUrl(url, { timeoutMs: 25000 })
    if (!r?.success || typeof r.base64 !== 'string') {
      throw new Error(typeof r?.error === 'string' ? r.error : 'electron_fetch_failed')
    }
    const bin = atob(r.base64)
    const bytes = new Uint8Array(bin.length)
    for (let i = 0; i < bin.length; i++) {
      bytes[i] = bin.charCodeAt(i)
    }
    const ct =
      typeof r.contentType === 'string' && r.contentType.trim()
        ? r.contentType.trim().split(';')[0]
        : 'application/octet-stream'
    return new Blob([bytes], { type: ct })
  }
  const res = await axios.get(url, {
    responseType: 'blob',
    timeout: 20000,
    validateStatus: (s) => s === 200,
    headers: {
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache'
    }
  })
  return res.data
}

/**
 * Download logo and encode as 1bpp BMP (base64) for Volcora IPC.
 * @param {string} logoUrl raw from business info
 * @param {number} paperWidthMm
 * @returns {Promise<string|null>}
 */
export async function fetchBusinessLogoAsBmpBase64 (logoUrl, paperWidthMm) {
  if (typeof document === 'undefined' || typeof createImageBitmap !== 'function') {
    return null
  }
  const url = resolveLogoRequestUrl(logoUrl)
  if (!url) return null

  const cacheKey = `${logoObjectKeyFromUrl(url)}:${paperWidthMm}:${LOGO_BMP_CACHE_VERSION}`
  if (logoBmpCache.key === cacheKey && logoBmpCache.bmp && logoBmpCache.expiresAt > Date.now()) {
    return logoBmpCache.bmp
  }

  let bitmap
  try {
    const blob = await fetchLogoImageBlob(url)
    bitmap = await createImageBitmap(blob)
    const maxW = getThermalLogoMaxWidth(paperWidthMm)
    const b64 = imageBitmapToMonoBmpBase64(bitmap, maxW, DEFAULT_LOGO_MAX_HEIGHT)
    if (b64) {
      logoBmpCache = {
        key: cacheKey,
        bmp: b64,
        expiresAt: Date.now() + LOGO_BMP_CACHE_MS
      }
    }
    return b64 || null
  } catch (e) {
    if (import.meta.env.QUASAR_DEV) {
      console.warn('[Thermal] logo download/rasterize failed:', url, e?.message || e)
    }
    return null
  } finally {
    try {
      bitmap?.close?.()
    } catch {
      // ignore
    }
  }
}
